import React, { FC, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { CustomerValues } from '../My';
import { DetailsValues } from '../../../utils/getInitialCustomerInfoValues';
import Formed from '../../Formed';
import useCreateDetailsSchema from '../../../hooks/useCreateDetailsSchema';
import FormActions from '@oberoninternal/travelbase-ds/components/form/FormActions';
import { Box } from '@rebass/grid';
import { FormattedMessage, useIntl } from 'react-intl';
import formActionsMessages from '../../../constants/formActionsMessages';
import { useUpdateCustomerMutation } from '../../../generated/graphql';
import { useDefaultCountry } from '../../../hooks/useDetailsFormOptions';
import getBirthdate from '../../../utils/getBirthdate';
import styled from 'styled-components';
import SettingsDetailsForm from '../../SettingsDetailsForm';
import LogoutSection from './LogoutSection';

interface Props extends CustomerValues {
    customerId: string;
}

const UserSettings: FC<React.PropsWithChildren<Props>> = ({
    customerId,
    birthdate,
    address,
    firstName,
    lastName,
    phoneNumber,
    email,
}) => {
    const createDetailsSchema = useCreateDetailsSchema();
    const [navActive, setNavActive] = useState<MutableRefObject<HTMLDivElement | null>>();
    const [logoutSection, setLogoutSection] = useState(false);

    const schema = useMemo(createDetailsSchema, [createDetailsSchema]);
    const { formatMessage } = useIntl();
    const initialCountry = useDefaultCountry();
    const [mutate] = useUpdateCustomerMutation();

    const ref1 = useRef(null);
    const ref2 = useRef(null);

    useEffect(() => {
        setNavActive(ref1);
    }, []);

    const onSetNavItem = (navRef: MutableRefObject<HTMLDivElement | null>, navSection: boolean) => {
        setNavActive(navRef);
        setLogoutSection(navSection);
    };

    return (
        <UserSettingsWrapper>
            <SideNavWrapper>
                <ul>
                    <NavItem active={navActive === ref1} onClick={() => onSetNavItem(ref1, false)}>
                        <FormattedMessage defaultMessage="Mijn gegevens" />
                    </NavItem>
                    <NavItem active={navActive === ref2} onClick={() => onSetNavItem(ref2, true)}>
                        <FormattedMessage defaultMessage="Uitloggen" />
                    </NavItem>
                </ul>
            </SideNavWrapper>
            <LogoutSectionWrapper>
                <LogoutSection />
            </LogoutSectionWrapper>
            {!logoutSection && (
                <FormWrapper>
                    <Formed<DetailsValues>
                        initialValues={{
                            customerInfo: {
                                address: {
                                    city: address?.city ?? '',
                                    countryCode: address?.countryCode ?? initialCountry,
                                    number: address?.number ?? '',
                                    postalCode: address?.postalCode ?? '',
                                    street: address?.street ?? '',
                                },
                                firstName: firstName ?? '',
                                lastName: lastName ?? '',
                                phoneNumber: phoneNumber ?? '',
                                birthdate: birthdate ?? '',
                                email: email ?? '',
                            },
                            birthdate: getBirthdate(birthdate),
                        }}
                        handleSubmit={async ({ customerInfo, birthdate: { day, month, year } }) => {
                            const birthdateInput = year && month && day ? `${year}-${month}-${day}` : '';

                            await mutate({
                                variables: {
                                    input: {
                                        customerId,
                                        address: customerInfo.address,
                                        birthdate: birthdateInput,
                                        firstName: customerInfo.firstName,
                                        lastName: customerInfo.lastName,
                                        phoneNumber: customerInfo.phoneNumber,
                                    },
                                },
                            });
                        }}
                        enableReinitialize
                        validateOnMount
                        validationSchema={schema}
                    >
                        {formikBag => (
                            <>
                                <Box>
                                    <SettingsDetailsForm touchErrorFields disableEmailField />
                                </Box>

                                <FormActions
                                    {...formikBag}
                                    alwaysVisible
                                    confirmMessage={formatMessage(formActionsMessages.confirm)}
                                    buttonText={<FormattedMessage defaultMessage="Gegevens opslaan" />}
                                    dataCyButton="save"
                                    resetText={<FormattedMessage defaultMessage="Wijzigingen ongedaan maken" />}
                                    dataCyReset="reset"
                                    buttonSize="large"
                                    isSimpleButton
                                />
                            </>
                        )}
                    </Formed>
                </FormWrapper>
            )}
            {logoutSection && <LogoutSection />}
        </UserSettingsWrapper>
    );
};

export default UserSettings;

const UserSettingsWrapper = styled.div`
    padding-top: 6.7rem;
    display: flex;

    @media (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding-top: 4.1rem;
        display: block;
    }
`;

const SideNavWrapper = styled.div`
    width: 20%;

    @media (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: none;
    }
`;

const FormWrapper = styled.div`
    width: 80%;

    @media (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 100%;
    }
`;

const NavItem = styled.li<{ active?: boolean }>`
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
    padding-left: 1.6rem;
    border-left: 1px solid ${({ theme, active }) => (active ? theme.colors.primary[80] : 'none')};
    color: ${({ theme, active }) => active && theme.colors.primary[80]};
    margin-bottom: 1.6rem;
    cursor: pointer;
`;

const LogoutSectionWrapper = styled.div`
    display: none;

    @media (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: block;
    }
`;
